import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useMySharerooms } from "@domain/group/hooks";
import { Course } from "@domain/lms/course/course";
import { getDisplayedAuthor } from "@domain/lms/utils";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { useModalTools } from "@ui/modals/modalContext";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { createToast } from "@ui/toasts/utils";
import { HookWrapper } from "@ui/components/hookWrapper";
import { FavoriteOffIcon, PlusIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { MenuRow } from "@ui/components/menus/menuRow";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { EditReferencePostModal } from "@ui/components/post/reference/editReferencePostModal";
import { CourseShareroomsModal } from "@ui/components/course/courseSharerooms/courseWorkspacesModal";

interface CourseThumbnailMenuProps extends React.HTMLAttributes<HTMLDivElement> {
	course: Course;
}

export const CourseThumbnailMenu: React.FC<CourseThumbnailMenuProps> = ({ course, ...htmlProps }) => {
	const { courseService } = useServices();
	const askDeleteConfirmation = useConfirmation(
		I18n.t("course.delete.title"),
		I18n.t("delete"),
		I18n.t("course.delete.message")
	);
	const history = useHistory();
	const currentProfile = useCurrentProfile();
	const displayedAuthor = getDisplayedAuthor(course);
	const subAuthor = displayedAuthor !== course.author ? course.author : undefined;
	const isAuthor = displayedAuthor.id === currentProfile?.id || subAuthor?.id === currentProfile?.id;
	const [favorite, setFavorite] = useState(course.isFavorite);
	const { createModal } = useModalTools();

	return (
		<KebabMenu {...htmlProps} authOnly>
			<SimpleMenuContent
				onShare={
					course.status === "DRAFT"
						? undefined
						: () => {
								return createModal((close) => (
									<EditReferencePostModal close={close} shared={{ type: "SHARE_COURSE", reference: course }} />
								));
							}
				}
				onEdit={isAuthor ? () => history.push(Path.Lms.Editor(course.id)) : undefined}
				onDelete={
					isAuthor
						? () =>
								askDeleteConfirmation(async () => {
									await courseService.deleteCourse(course.id);
									createToast(I18n.t("course.toast.delete.title"), I18n.t("course.toast.delete.message"), "success");
								})
						: undefined
				}
				reportInfos={
					isAuthor
						? undefined
						: {
								contentId: course.id,
								contentType: "COURSE",
							}
				}
			>
				{!isAuthor && (
					<MenuRow
						onClick={async () => {
							await courseService.toggleFavorite({ ...course, isFavorite: favorite });
							setFavorite(!favorite);
						}}
					>
						<IconWrapper>
							<FavoriteOffIcon toggled={favorite} />
						</IconWrapper>
						<span>{I18n.t("favorite")}</span>
					</MenuRow>
				)}
				<HookWrapper hook={() => useMySharerooms()}>
					{({ result }) => {
						const filteredRooms = result.filter(({ userRole }) => userRole === "ADMINISTRATOR");
						return filteredRooms.flatMap((room) => room.workspaces ?? []).length > 0 ? (
							<MenuRow
								onClick={() =>
									createModal((close) => (
										<CourseShareroomsModal close={close} course={course} sharerooms={filteredRooms} />
									))
								}
							>
								<IconWrapper>
									<PlusIcon />
								</IconWrapper>
								<span>{I18n.t("course.workspaces.add")}</span>
							</MenuRow>
						) : null;
					}}
				</HookWrapper>
			</SimpleMenuContent>
		</KebabMenu>
	);
};

const IconWrapper = styled.div`
	flex-shrink: 0;
	border-radius: 9px;
	width: 34px;
	height: 34px;
	background-color: ${({ theme }) => theme.colors.secondaryLight};
	${row(0, "center", "center")};

	> svg path {
		fill: ${({ theme }) => theme.colors.secondary};
	}
`;
